import { Box } from '@atlaskit/primitives';
import {
  EditableListWithPlaceholder,
  type EditableListProps,
} from '@databases/editable-list';
import JiraIssuePicker from '@databases/field-picker/JiraIssuePicker';
import { type JiraIssueRef } from '@databases/types/confluence';
import { DatabaseFieldType } from '@databases/types/database';
import { MultiValueChangeType } from '@databases/utils/database/changesets';
import { isNonEmptyJiraIssueValue } from '@databases/utils/database/field-types/jiraIssue';
import { generateId } from '@databases/utils/utils';
import React, { type FunctionComponent, useCallback, useMemo, useState } from 'react';

import { type EditComponentProps } from './types';

type Value = (JiraIssueRef | object) & { _id: string };

function isNonEmptyValue(val: Value): val is JiraIssueRef & { _id: string } {
  return isNonEmptyJiraIssueValue(val);
}

const getId = (value: Value): string => value._id;

const getNewValue = (): Value => ({ _id: generateId() });

/**
 * @deprecated This component is deprecated and can be removed since cell
 * interactions rollout. Edit now uses `JiraIssuePopoverEdit`.
 */
const JiraIssueValueEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.JIRA_ISSUE>
> = ({ field, value, onChange, onFocus, onBlur, autoFocus, isSeamless }) => {
  const resolvedValue = useMemo(
    () =>
      value.map((v) =>
        isNonEmptyJiraIssueValue(v) ? { ...v, serverId: field.settings.jiraServerId } : v
      ),
    [field.settings.jiraServerId, value]
  );
  const isMulti = field.settings.multi || resolvedValue.length > 1;

  const [inputValue, setInputValue] = useState<Value[]>(resolvedValue);

  const handleSingleChange = useCallback(
    (val: JiraIssueRef | undefined) => {
      const singleJiraIssue =
        inputValue.length === 0
          ? { ...(val ?? {}), _id: generateId() }
          : { ...(val ?? {}), _id: inputValue[0]._id };

      if (!val && inputValue.length === 0) {
        return;
      }

      setInputValue(val ? [singleJiraIssue] : []);

      onChange(val ? [singleJiraIssue] : [], {
        type: DatabaseFieldType.JIRA_ISSUE,
        delta: val
          ? inputValue.length === 0
            ? {
                type: MultiValueChangeType.APPEND,
                value: singleJiraIssue,
              }
            : {
                type: MultiValueChangeType.EDIT,
                index: 0,
                value: singleJiraIssue,
              }
          : {
              type: MultiValueChangeType.REMOVE,
              index: 0,
            },
      });
    },
    [inputValue, onChange]
  );

  const renderField: EditableListProps<Value, null>['renderField'] = useCallback(
    ({ value, autoFocus, onChange }) => (
      <Box style={{ width: '100%' }}>
        <JiraIssuePicker
          cloudId={field.settings.cloudId}
          serverId={field.settings.jiraServerId}
          value={isNonEmptyValue(value) ? value : undefined}
          onChange={(val) => {
            onChange({ ...(val ?? {}), _id: value._id }, null);
          }}
          autoFocus={autoFocus}
          isSeamless
          isLinkPreview
        />
      </Box>
    ),
    [field.settings.cloudId, field.settings.jiraServerId]
  );

  const onItemAdd = React.useCallback(
    (val: Value, allValues: Value[]) => {
      setInputValue(allValues);

      onChange(allValues, {
        type: DatabaseFieldType.JIRA_ISSUE,
        delta: {
          type: MultiValueChangeType.APPEND,
          value: val,
        },
      });
    },
    [onChange]
  );

  const onItemDelete = React.useCallback(
    (val: Value, removeIndex: number, allValues: Value[]) => {
      setInputValue(allValues);

      onChange(allValues, {
        type: DatabaseFieldType.JIRA_ISSUE,
        delta: {
          type: MultiValueChangeType.REMOVE,
          index: removeIndex,
        },
      });
    },
    [onChange]
  );

  const onItemChange = React.useCallback(
    async (val: Value, newIndex: number, delta: null, allValues: Value[]) => {
      setInputValue(allValues);

      onChange(allValues, {
        type: DatabaseFieldType.JIRA_ISSUE,
        delta: {
          type: MultiValueChangeType.EDIT,
          index: newIndex,
          value: val,
        },
      });
    },
    [onChange]
  );

  const onItemReorder = React.useCallback(
    (val: Value, currentIndex: number, newIndex: number, allValues: Value[]) => {
      setInputValue(allValues);

      onChange(allValues, {
        type: DatabaseFieldType.JIRA_ISSUE,
        delta: {
          type: MultiValueChangeType.REORDER,
          currentIndex,
          newIndex,
        },
      });
    },
    [onChange]
  );

  if (isMulti) {
    return (
      <EditableListWithPlaceholder<Value, null>
        getId={getId}
        values={resolvedValue}
        onItemChange={onItemChange}
        onAddNewItem={onItemAdd}
        onRemoveItem={onItemDelete}
        onReorder={onItemReorder}
        getNewValue={getNewValue}
        renderField={renderField}
        autoFocus={value.length === 0 ? autoFocus : undefined}
        addLabel="Add issue"
        noAdd={!field.settings.multi}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    );
  } else {
    return (
      <JiraIssuePicker
        cloudId={field.settings.cloudId}
        serverId={field.settings.jiraServerId}
        value={
          inputValue.length > 0
            ? isNonEmptyValue(inputValue[0])
              ? inputValue[0]
              : undefined
            : undefined
        }
        onChange={handleSingleChange}
        onFocus={onFocus}
        onBlur={onBlur}
        autoFocus={autoFocus}
        isClearable
        isSeamless={isSeamless}
        isLinkPreview
      />
    );
  }
};

export default JiraIssueValueEdit;
